import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { useClientConfig } from '../lib/useClientConfig';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { safeReturnTo, withReturnTo } from '../lib/returnTo';

interface LocationState {
  from?: string;
  returnTo?: string;
  /** The address an invite was sent to (TOM-458): the form starts with it. */
  email?: string;
}

// The Google callback could not match the browser's sign-in state: it outlived
// its ten-minute window (phone 2FA or a slow account picker can take that long),
// it finished in another browser, or a newer sign-in replaced it.
const GOOGLE_STATE_LOST =
  'That Google sign-in took too long or finished in another browser. Try again.';

// Errors the OIDC callback (and other auth flows) can hand off via
// ?error=. Unknown values render no banner — failing closed avoids
// surfacing arbitrary strings the URL can carry.
const ERROR_BANNERS: Record<string, string> = {
  identity_conflict: 'An account already exists for that email — sign in with email and password.',
  email_in_use:
    'An account already exists for that email — sign in with Google or with your password.',
};

// The Google callback's retryable failures, also handed off via ?error= (task
// google_slow_retry). Their banner sits above "Continue with Google", the button
// it asks the owner to press again, rather than in the password form.
const GOOGLE_RETRY_BANNERS: Record<string, string> = {
  oidc_state_missing: GOOGLE_STATE_LOST,
  oidc_state_invalid: GOOGLE_STATE_LOST,
  oidc_state_mismatch: GOOGLE_STATE_LOST,
  // Google answered with an error instead of a code: the owner cancelled on its
  // screen, or Google could not finish.
  oidc_provider_error: 'Google sign-in was cancelled or didn’t finish. Try again.',
};

// Success notices handed off via the `?reset=<value>` query (e.g. the
// reset-password confirm bounce sends `?reset=success`). Keyed by the
// param value; unknown values render no banner. Non-error banner above
// the form.
const RESET_BANNERS: Record<string, string> = {
  success: 'Your password has been reset. Sign in with your new password.',
};

// Inline link-style button for the in-banner retry action (no shared class).
const linkButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'inherit',
  font: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer',
};

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="auth-banner auth-banner--error" role="alert">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M12 9v4" strokeLinecap="round" />
        <path
          d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
      </svg>
      <ul className="form-errors" data-testid="error-banner">
        <li>{message}</li>
      </ul>
    </div>
  );
}

export function Login() {
  useDocumentTitle('Sign in - Neomaris');
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Prefer the router state (set on the in-app /activate→/login bounce and
  // the invite page's Sign in). Fall back to the ?from= query,
  // which is how a 302 redirect (e.g. the Google OIDC identity_conflict
  // hand-off) carries the destination across a full page load where router
  // state is lost. Anything off the allowlist is ignored downstream.
  const from =
    (location.state as LocationState | null)?.from ?? searchParams.get('from') ?? '/dashboard';
  // activation_return_link: the boat origin to return to after activation —
  // from router state (in-app bounce) or the ?return_to= query (the OIDC
  // identity_conflict 302 hand-off). Guarded to http(s) before we carry it on.
  const returnTo = safeReturnTo(
    (location.state as LocationState | null)?.returnTo ?? searchParams.get('return_to')
  );
  const dest = withReturnTo(from, returnTo);
  const errorParam = searchParams.get('error') ?? '';
  const initialBanner = ERROR_BANNERS[errorParam] ?? null;
  const successBanner = RESET_BANNERS[searchParams.get('reset') ?? ''] ?? null;

  const [email, setEmail] = useState((location.state as LocationState | null)?.email ?? '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  // "Keep me signed in" (task remember_me): opt in to a 365-day session instead
  // of the 24-hour default. Defaults to off — a longer session is something
  // the user asks for, never something we assume on a shared machine. Read by
  // both sign-in paths: the password submit below and the Google button, which
  // carries it to /v1/auth/google/start.
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(initialBanner);
  const [googleError, setGoogleError] = useState<string | null>(
    GOOGLE_RETRY_BANNERS[errorParam] ?? null
  );

  // Step-up Turnstile (threat A1). The widget appears only after the server
  // signals `captchaRequired` — i.e. after a failed attempt from this IP, so
  // the first sign-in stays frictionless. The site key comes from
  // GET /v1/config; a null key (local dev / test) means no widget and
  // server-side verification is bypassed or fails open.
  const { config: clientConfig } = useClientConfig();
  const turnstileSiteKey = clientConfig?.turnstileSiteKey ?? null;
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const [turnstileWidgetKey, setTurnstileWidgetKey] = useState(0);
  const [turnstileFailed, setTurnstileFailed] = useState(false);

  const needsToken = captchaRequired && turnstileSiteKey !== null;
  const canSubmit = !submitting && (!needsToken || turnstileToken !== null);

  function retryTurnstile() {
    setTurnstileFailed(false);
    setTurnstileWidgetKey((k) => k + 1);
  }

  // Already signed in? Skip the form.
  useEffect(() => {
    if (auth.isAuthenticated) navigate(dest, { replace: true });
  }, [auth.isAuthenticated, dest, navigate]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Guard the Enter path: never submit without a token once the challenge
    // is active (the button is disabled too).
    if (needsToken && turnstileToken === null) return;
    setError(null);
    setGoogleError(null);
    setSubmitting(true);
    try {
      await auth.login(email, password, turnstileToken, remember);
      navigate(dest, { replace: true });
    } catch (err) {
      const e = err as {
        status?: number;
        body?: { message?: string; captchaRequired?: boolean };
        message?: string;
      };
      // The server steps up to a Turnstile challenge after a failed attempt.
      if (e.body?.captchaRequired === true) setCaptchaRequired(true);
      // The token is single-use; this failed/blocked submit consumed it.
      if (turnstileSiteKey !== null) {
        setTurnstileToken(null);
        setTurnstileNonce((n) => n + 1);
      }
      setError(e.body?.message ?? 'Invalid email or password.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="auth-panel">
      <div className="auth-card auth-card-v2">
        <h2>Sign in</h2>
        <p className="auth-sub">Welcome back. Sign in to keep watch over your boat.</p>
        {googleError !== null ? <ErrorBanner message={googleError} /> : null}
        <GoogleSignInButton from={from} returnTo={returnTo} remember={remember} />
        <div className="auth-divider" role="separator" aria-label="or">
          <span>or</span>
        </div>
        {successBanner !== null && error === null ? (
          <div
            className="auth-banner auth-banner--success"
            role="status"
            data-testid="success-banner"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="12" cy="12" r="9" />
            </svg>
            <span className="banner-text">{successBanner}</span>
          </div>
        ) : null}
        <form onSubmit={onSubmit} data-testid="login-form">
          {error !== null ? <ErrorBanner message={error} /> : null}
          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <input
              type="email"
              id="login-email"
              className="form-input"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <div className="field-password">
              <label htmlFor="login-password">Password</label>
              <input
                type={showPw ? 'text' : 'password'}
                id="login-password"
                className="form-input"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="button" className="pw-toggle" onClick={() => setShowPw((v) => !v)}>
                <span className="sr-only">{showPw ? 'Hide password' : 'Show password'}</span>
                {showPw ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="M3 3l18 18" strokeLinecap="round" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" strokeLinecap="round" />
                    <path
                      d="M9.4 5.2A9.6 9.6 0 0 1 12 5c5 0 9 4.5 9 7 0 1-1 2.6-2.7 4M6.3 6.3C3.9 7.8 3 10 3 12c0 1.4 1.9 4 5 5.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {/* DOM order is load-bearing: field → help → aside. The grid puts the
                link top-right on desktop; reordering breaks the mobile stack. */}
            <div className="field-foot">
              <div className="auth-checkbox-field">
                <input
                  type="checkbox"
                  id="login-remember"
                  className="auth-checkbox-input"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  disabled={submitting}
                  aria-describedby="login-remember-help"
                  data-testid="remember-me"
                />
                <label className="auth-checkbox-label" htmlFor="login-remember">
                  Keep me signed in
                </label>
              </div>
              {/* The session length is deliberately not on screen. ADR 0190
                  named the duration as part of its residual-risk mitigation;
                  the surviving half — the shared-computer warning — is the half
                  that changes what a user does at the tick. */}
              <p className="auth-checkbox-help" id="login-remember-help">
                For your own phone or laptop, not a shared computer.
              </p>
              <p className="form-aside">
                <Link to="/forgot-password">Forgot password?</Link>
              </p>
            </div>
          </div>
          {captchaRequired && turnstileSiteKey !== null && !turnstileFailed ? (
            <div className="form-group">
              <TurnstileWidget
                key={turnstileWidgetKey}
                siteKey={turnstileSiteKey}
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileFailed(true)}
                resetSignal={turnstileNonce}
              />
            </div>
          ) : null}
          {captchaRequired && turnstileSiteKey !== null && turnstileFailed ? (
            <div className="auth-banner auth-banner--error" role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 9v4" strokeLinecap="round" />
                <path
                  d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <ul className="form-errors" data-testid="turnstile-error-banner">
                <li>
                  We couldn’t finish a quick security check — usually a weak connection.{' '}
                  <button type="button" style={linkButtonStyle} onClick={retryTurnstile}>
                    Retry
                  </button>
                  , or try again in a moment.
                </li>
              </ul>
            </div>
          ) : null}
          <button type="submit" className="btn btn-primary auth-submit" disabled={!canSubmit}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p className="auth-foot">
          {/* While in beta, everyone new joins the beta list, whatever page
            sent them here: no public page leads to /signup (TOM-888, ADR 0435). */}
          New here? <Link to="/beta">Join the beta</Link>
        </p>
      </div>
    </section>
  );
}
